import React, { useEffect, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase"; // Assicurati che l'import sia corretto

const AuthCallback: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { hash } = useLocation();
    const {
        isAuthenticated,
        user,
        organization,
        loading,
        completeOnboardingAfterConfirmation,
        logout,
    } = useAuth();

    // Ref per evitare doppia esecuzione in React Strict Mode
    const calledRef = useRef(false);

    useEffect(() => {
        const handleCallback = async () => {
            // Supabase può restituire parametri nell'URL o nell'hash frammento
            const hashParams = new URLSearchParams(
                hash?.startsWith("#") ? hash.slice(1) : ""
            );

            const error = searchParams.get("error") || hashParams.get("error");
            const errorDescription =
                searchParams.get("error_description") ||
                hashParams.get("error_description");

            const type = searchParams.get("redirect_type") || "";

            // 1. GESTIONE ERRORI SUPABASE
            if (error || (!type && !user)) {
                let message = "Errore durante l'autenticazione. Riprova.";
                if (errorDescription?.includes("expired")) {
                    message = "Il link di conferma è scaduto. Richiedi una nuova email.";
                } else if (errorDescription?.includes("already been used")) {
                    message = "Questo link è già stato utilizzato. Puoi accedere direttamente.";
                } else if (!type && !user) {
                    message = "Link non valido o sessione scaduta.";
                }

                toast.error(message);

                setTimeout(() => {
                    navigate("/login", { replace: true });
                }, 1500);
                return;
            }

            // 2. ATTESA DEL CARICAMENTO DELLA SESSIONE
            if (loading) return;

            // Se non siamo autenticati e non c'è un errore esplicito, qualcosa non va
            if (!isAuthenticated || !user) return;

            // 3. LOGICA DI REINDIRIZZAMENTO BASATA SUL TIPO
            if (type === "invite") {
                // Caso: Accettazione invito dipendente
                if (calledRef.current) return;
                calledRef.current = true;

                const res = await completeOnboardingAfterConfirmation();
                if (res.success) {
                    toast.success("Account attivato con successo!");
                    navigate("/", { replace: true });
                } else {
                    toast.error(res?.error || "Errore durante l'attivazione. Riprova.");
                    await logout();
                    navigate("/login", { replace: true });
                }
            } else if (type === "recovery") {
                // Caso: Reset Password
                if (calledRef.current) return;
                calledRef.current = true;

                // L'utente è già autenticato dal link magico
                toast.success("Accesso effettuato. Imposta la tua nuova password.");
                navigate("/reset", { replace: true });
            } else if (type === "signup" || type === "oauth") {
                // Caso: Nuova registrazione Admin o Login Social
                if (calledRef.current) return;
                calledRef.current = true;

                // GOOGLE LOGIN CON INVITO
                const inviteToken = localStorage.getItem('clip_invite_token');
                if (inviteToken) {
                    const res = await completeOnboardingAfterConfirmation();

                    if (res.success) {
                        toast.success("Invito accettato con successo!");
                        navigate("/", { replace: true });
                        return;
                    } else {
                        toast.error(res.error || "Errore durante l'accettazione dell'invito.");
                        await logout();
                        navigate("/login", { replace: true });
                        return;
                    }
                }

                toast.success("Autenticazione completata!");

                // Controlliamo lo stato nel context per decidere dove mandarlo
                // Nota: usiamo 'organization' dal context che potrebbe essere null se l'utente
                // non ha ancora un salone (anche se è appena stato promosso a barbiere).
                if (!organization) {
                    // Ha l'account auth (ora aggiornato) ma non il negozio -> New Shop
                    navigate("/new-shop", { replace: true });
                } else {
                    // Ha tutto -> Dashboard (o Subscription se non ha pagato, gestito da PublicRoute/App)
                    navigate("/", { replace: true });
                }
            } else {
                // Fallback generico
                navigate("/", { replace: true });
            }
        };

        handleCallback();
    }, [
        user,
        isAuthenticated,
        organization,
        loading,
        navigate,
        searchParams,
        hash,
        completeOnboardingAfterConfirmation,
        logout,
    ]);

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-gray-900 font-medium animate-pulse">
                Finalizzazione autenticazione...
            </p>
        </div>
    );
};

export default AuthCallback;